import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  Chip,
  Paper,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Pagination,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Skeleton,
  Avatar,
  Stack,
  Switch,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { 
  Hotel,
  Person,
  Event,
  CleaningServices,
  People,
  Build,
  FindInPage,
  ChecklistRtl,
  Login as LoginIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import moment from 'moment';
import DashboardHeader from '../components/common/DashboardHeader';
import NotchTabs from '../components/common/NotchTabs';
import EmptyStateCard from '../components/common/EmptyStateCard';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import {
  getConfigurationRooms,
  getStayingBookings,
  getRoomCleanings,
  getMaintenanceRequests,
  getHousekeepingSettings,
  updateHousekeepingSettings,
} from '../services/api';
import HousekeepingBookingTimeline from '../components/housekeeping/HousekeepingBookingTimeline';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon,
  Hotel as HotelIcon,
} from '@mui/icons-material';
import HousekeepingStaffSection from '../components/housekeeping/HousekeepingStaffSection';
import RoomCleaningSection from '../components/housekeeping/RoomCleaningSection';
import MaintenanceRequestSection from '../components/housekeeping/MaintenanceRequestSection';
import LostAndFoundSection from '../components/housekeeping/LostAndFoundSection';
import HousekeepingConfigSection from '../components/housekeeping/HousekeepingConfigSection';
import DashboardHousekeepingDrawer from '../components/dashboard/DashboardHousekeepingDrawer';
import ErrorState from '../components/common/ErrorState';
import { getDashboardPageShellSx } from '../utils/dashboardSurfaceSx';
import { resolveWalkInHousekeepingCleaningId } from '../utils/walkInHousekeeping';
import {
  getHousekeepingPanelPaperSx,
  getHousekeepingRoomCardSx,
  getHousekeepingTabsPaperSx,
  getHousekeepingThemeColors,
} from '../utils/housekeepingMobileLayout';

const CUSTOM_BORDER_COLOR = '#4ce1b6';

const HOUSEKEEPING_TABS = [
  {
    icon: CleaningServices,
    label: 'Room Status',
    shortLabel: 'Status',
    path: '/housekeeping/room-status/',
  },
  {
    icon: CleaningServices,
    label: 'Room Cleanings',
    shortLabel: 'Cleanings',
    path: '/housekeeping/cleanings/',
  },
  {
    icon: People,
    label: 'Staff',
    path: '/housekeeping/staff/',
  },
  {
    icon: Build,
    label: 'Maintenance',
    shortLabel: 'Maint.',
    path: '/housekeeping/maintenance/',
  },
  {
    icon: FindInPage,
    label: 'Lost & Found',
    shortLabel: 'Lost',
    path: '/housekeeping/lost-and-found/',
  },
  {
    icon: ChecklistRtl,
    label: 'Checklist',
    path: '/housekeeping/checklist/',
  },
];

const HOUSEKEEPING_DEFAULT_PATH = HOUSEKEEPING_TABS[0].path;

const resolveHousekeepingTabIndex = (pathname = '', tabParam = null) => {
  if (tabParam != null && tabParam !== '') {
    const fromQuery = parseInt(tabParam, 10);
    if (Number.isInteger(fromQuery) && fromQuery >= 0 && fromQuery < HOUSEKEEPING_TABS.length) {
      return fromQuery;
    }
  }

  const normalized = pathname.endsWith('/') ? pathname : `${pathname}/`;
  if (normalized === '/housekeeping/' || normalized === '/housekeeping') {
    return 0;
  }

  if (normalized === '/housekeeping/config/' || normalized.startsWith('/housekeeping/config/')) {
    return HOUSEKEEPING_TABS.findIndex((tab) => tab.path === '/housekeeping/checklist/');
  }

  const index = HOUSEKEEPING_TABS.findIndex(
    (tab) => normalized === tab.path || normalized.startsWith(tab.path),
  );
  return index >= 0 ? index : 0;
};

const HousekeepingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Initialize theme synchronously to prevent flash
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = currentTheme === 'dark';
  const themeColors = getHousekeepingThemeColors(isDark);
  const panelBorder = themeColors.surfaceBorder;

  // Watch for theme changes
  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  const [rooms, setRooms] = useState([]);
  const [arrivalReloadKey, setArrivalReloadKey] = useState(0);
  const [maintenanceRequests, setMaintenanceRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [filterType, setFilterType] = useState('all'); // 'all', 'dirty', 'clean', 'occupied', 'outOfOrder'
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12; // 3 rows × 4 cards per row
  const [autoCleanOnCheckout, setAutoCleanOnCheckout] = useState(false);
  const [autoCleanLoading, setAutoCleanLoading] = useState(false);
  const [autoCleanSaving, setAutoCleanSaving] = useState(false);
  const [pendingCleaningByRoomId, setPendingCleaningByRoomId] = useState({});
  const [housekeepingCleaningId, setHousekeepingCleaningId] = useState(null);
  const [housekeepingResolving, setHousekeepingResolving] = useState(false);
  const [housekeepingError, setHousekeepingError] = useState('');
  const canManageHousekeepingSettings = useMemo(() => {
    const userGroupNames = Array.isArray(window.DJANGO_CONTEXT?.user_groups)
      ? window.DJANGO_CONTEXT.user_groups.map((groupName) => String(groupName || '').trim().toLowerCase())
      : [];
    return userGroupNames.includes('owner') || userGroupNames.includes('general manager');
  }, []);
  const activeTab = useMemo(
    () => resolveHousekeepingTabIndex(location.pathname, searchParams.get('tab')),
    [location.pathname, searchParams],
  );

  // Legacy ?tab=N links → canonical path URLs
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam == null || tabParam === '') return;
    const tabIndex = resolveHousekeepingTabIndex(location.pathname, tabParam);
    const targetPath = HOUSEKEEPING_TABS[tabIndex]?.path || HOUSEKEEPING_DEFAULT_PATH;
    navigate(targetPath, { replace: true });
  }, [location.pathname, navigate, searchParams]);

  // Keep bare /housekeeping/ on the default tab path so the URL always reflects the tab
  useEffect(() => {
    if (searchParams.get('tab')) return;
    const normalized = location.pathname.endsWith('/')
      ? location.pathname
      : `${location.pathname}/`;
    if (normalized === '/housekeeping/') {
      navigate(HOUSEKEEPING_DEFAULT_PATH, { replace: true });
    }
    if (normalized === '/housekeeping/config/' || normalized.startsWith('/housekeeping/config/')) {
      navigate('/housekeeping/checklist/', { replace: true });
    }
  }, [location.pathname, navigate, searchParams]);

  const handleTabChange = useCallback((_, newValue) => {
    const nextPath = HOUSEKEEPING_TABS[newValue]?.path || HOUSEKEEPING_DEFAULT_PATH;
    if (location.pathname !== nextPath) {
      navigate(nextPath);
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    fetchHousekeepingData();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadSettings = async () => {
      setAutoCleanLoading(true);
      try {
        const data = await getHousekeepingSettings();
        if (!cancelled) {
          setAutoCleanOnCheckout(Boolean(data?.auto_clean_on_checkout));
        }
      } catch {
        // Keep default off if settings fail to load
      } finally {
        if (!cancelled) setAutoCleanLoading(false);
      }
    };
    loadSettings();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAutoCleanToggle = useCallback(async (event) => {
    const nextValue = event.target.checked;
    const previous = autoCleanOnCheckout;
    setAutoCleanOnCheckout(nextValue);
    setAutoCleanSaving(true);
    try {
      const data = await updateHousekeepingSettings({ auto_clean_on_checkout: nextValue });
      setAutoCleanOnCheckout(Boolean(data?.auto_clean_on_checkout));
    } catch {
      setAutoCleanOnCheckout(previous);
    } finally {
      setAutoCleanSaving(false);
    }
  }, [autoCleanOnCheckout]);

  const fetchHousekeepingData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const dateStr = moment().format('YYYY-MM-DD');

      // Fetch rooms, staying bookings, cleanings, and maintenance in parallel
      // Arrival board loads its own pages (15 at a time) separately for performance.
      const [roomsByFloor, stayingResult, cleaningsResult, maintenanceResult] = await Promise.all([
        getConfigurationRooms(),
        getStayingBookings(dateStr).then((data) => data?.bookings || []).catch(() => []),
        getRoomCleanings({ cleaning_date: dateStr, page_size: 500 }).then((data) => data?.results || []).catch(() => []),
        getMaintenanceRequests({ page_size: 500 }).then((data) => data?.results || []).catch(() => []),
      ]);
      const stayingBookings = Array.isArray(stayingResult) ? stayingResult : [];
      const todayCleanings = Array.isArray(cleaningsResult) ? cleaningsResult : [];
      const allMaintenanceRequests = Array.isArray(maintenanceResult) ? maintenanceResult : [];
      const openMaintenanceRequests = allMaintenanceRequests.filter((r) => (r.status || '').toUpperCase() === 'OPEN');
      const openMaintenanceByRoomId = new Map();
      openMaintenanceRequests.forEach((req) => {
        const roomId = typeof req.room === 'object' && req.room != null ? req.room.id : (req.room_id ?? req.room);
        if (roomId != null && roomId !== '') {
          const id = Number(roomId) || roomId;
          if (!openMaintenanceByRoomId.has(id)) openMaintenanceByRoomId.set(id, []);
          openMaintenanceByRoomId.get(id).push(req);
        }
      });

      // Room IDs that have a cleaning with status PENDING or IN_PROGRESS today → show as Dirty
      const roomIdsWithPendingOrInProgressCleaning = new Set(
        todayCleanings
          .filter((c) => c.status === 'PENDING' || c.status === 'IN_PROGRESS')
          .map((c) => (typeof c.room === 'object' && c.room != null ? c.room.id : c.room_id ?? c.room))
          .filter((id) => id != null && id !== '')
      );
      const pendingCleaningMap = todayCleanings
        .filter((c) => c.status === 'PENDING' || c.status === 'IN_PROGRESS')
        .reduce((acc, cleaning) => {
          const roomId = typeof cleaning.room === 'object' && cleaning.room != null
            ? cleaning.room.id
            : cleaning.room_id ?? cleaning.room;
          if (roomId == null || roomId === '' || !cleaning.id) return acc;
          const key = String(roomId);
          if (!acc[key] || cleaning.status === 'IN_PROGRESS') {
            acc[key] = cleaning.id;
          }
          return acc;
        }, {});

      // Create a map of room_id -> booking for quick lookup
      const roomBookingMap = new Map();
      stayingBookings.forEach(booking => {
        if (booking.allocated_rooms && booking.allocated_rooms.length > 0) {
          booking.allocated_rooms.forEach(room => {
            roomBookingMap.set(room.room_id, {
              bookingId: booking.id,
              bookingCode: booking.code,
              guestName: booking.guest_name || '',
              bookingStatus: booking.status || '',
              checkIn: booking.check_in_date ? moment(booking.check_in_date).format('DD-MMM-YYYY') : '',
              checkOut: booking.check_out_date ? moment(booking.check_out_date).format('DD-MMM-YYYY') : '',
            });
          });
        }
      });
      
      // Flatten rooms from all floors and enrich with booking data
      const allRooms = [];
      roomsByFloor.forEach(floor => {
        if (floor.rooms && floor.rooms.length > 0) {
          floor.rooms.forEach(room => {
            const bookingInfo = roomBookingMap.get(room.id);
            
            // Map clean_status to housekeeping status
            let housekeepingStatus = 'Clean';
            const cleanStatus = (room.clean_status || 'clean').toLowerCase();
            const roomStatus = (room.status || '').toLowerCase();
            const isOccupied = roomStatus === 'occupied' || !!bookingInfo;

            if (cleanStatus === 'dirty') {
              housekeepingStatus = 'Dirty';
            } else if (cleanStatus === 'clean') {
              if (isOccupied) {
                housekeepingStatus = 'Clean - Occupied';
              } else {
                housekeepingStatus = 'Clean';
              }
            } else if (cleanStatus === 'inspected') {
              housekeepingStatus = 'Clean';
            }

            // If room has a cleaning today with PENDING or IN_PROGRESS, show as Dirty
            if (roomIdsWithPendingOrInProgressCleaning.has(room.id)) {
              housekeepingStatus = 'Dirty';
            }

            // Override with Out Of Order if room status indicates maintenance/out of order
            if (roomStatus === 'maintenance' || roomStatus === 'out_of_order') {
              housekeepingStatus = 'Out Of Order';
            }
            
            allRooms.push({
              id: room.id,
              roomNumber: room.room_number,
              status: housekeepingStatus,
              floorId: floor.floor_id,
              floorName: floor.floor_display,
              floorNumber: floor.floor_number,
              roomTypeId: room.room_type_id,
              roomTypeName: room.room_type_name,
              roomTypeCode: room.room_type_code,
              roomTypeImageUrl: room.room_type_image_url || null,
              guestName: bookingInfo?.guestName || '',
              bookingStatus: bookingInfo?.bookingStatus || '',
              checkIn: bookingInfo?.checkIn || '',
              checkOut: bookingInfo?.checkOut || '',
              bookingId: bookingInfo?.bookingCode || '',
              staffName: '',
              staffRemark: room.notes || '',
              maintenanceRequests: openMaintenanceByRoomId.get(room.id) || [],
            });
          });
        }
      });
      
      setRooms(allRooms);
      setPendingCleaningByRoomId(pendingCleaningMap);
      setMaintenanceRequests(allMaintenanceRequests);
      setApiError(null);
      setError(null);
      // Arrival board self-loads on mount; bump only on later housekeeping refreshes.
      setArrivalReloadKey((key) => (hasFetched ? key + 1 : key));
      setHasFetched(true);
    } catch (err) {setApiError(err);
      // Only set simple error message for non-auth errors
      if (err?.statusCode !== 401 && err?.statusCode !== 403) {
        setError(err?.userMessage || 'Failed to load housekeeping data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDirtyRoomClick = useCallback(async (room) => {
    if (room?.status !== 'Dirty' || !room?.id || housekeepingResolving) return;
    setHousekeepingResolving(true);
    setHousekeepingError('');
    try {
      const cleaningId = await resolveWalkInHousekeepingCleaningId(room.id, pendingCleaningByRoomId);
      setHousekeepingCleaningId(cleaningId);
    } catch (err) {
      setHousekeepingError(err?.error || err?.detail || err?.message || 'Could not open cleaning drawer for this room.');
    } finally {
      setHousekeepingResolving(false);
    }
  }, [housekeepingResolving, pendingCleaningByRoomId]);

  const handleHousekeepingDrawerClose = useCallback(() => {
    setHousekeepingCleaningId(null);
  }, []);

  const handleHousekeepingDrawerSuccess = useCallback(async ({ roomId } = {}) => {
    setHousekeepingCleaningId(null);
    if (!roomId) {
      await fetchHousekeepingData();
      return;
    }

    const cleanedRoomId = Number(roomId);
    setRooms((prevRooms) => prevRooms.map((room) => {
      if (Number(room.id) !== cleanedRoomId) return room;
      return {
        ...room,
        status: room.guestName ? 'Clean - Occupied' : 'Clean',
      };
    }));
    setPendingCleaningByRoomId((prevMap) => {
      const nextMap = { ...prevMap };
      delete nextMap[String(roomId)];
      return nextMap;
    });
  }, [fetchHousekeepingData]);

  const handleHousekeepingDrawerError = useCallback((message) => {
    setHousekeepingError(message || 'Failed to mark room as clean.');
  }, []);

  const handleFilterChange = (event, newFilter) => {
    if (newFilter !== null) {
      setFilterType(newFilter);
      setCurrentPage(1); // Reset to first page when filter changes
    }
  };

  // Maintenance analytics derived from fetched requests
  const maintenanceAnalytics = useMemo(() => {
    const open = maintenanceRequests.filter((r) => (r.status || '').toUpperCase() === 'OPEN');
    const fixed = maintenanceRequests.filter((r) => (r.status || '').toUpperCase() === 'FIXED');
    const byPriority = {
      HIGH: open.filter((r) => (r.priority || '').toUpperCase() === 'HIGH').length,
      MEDIUM: open.filter((r) => (r.priority || '').toUpperCase() === 'MEDIUM').length,
      LOW: open.filter((r) => (r.priority || '').toUpperCase() === 'LOW').length,
    };
    const roomIdsWithOpen = new Set(
      open.map((r) => (typeof r.room === 'object' && r.room != null ? r.room.id : r.room_id ?? r.room)).filter((id) => id != null && id !== '')
    );
    const issueTypeCounts = {};
    open.forEach((r) => {
      const t = r.issue_type || 'Other';
      issueTypeCounts[t] = (issueTypeCounts[t] || 0) + 1;
    });
    const topIssueTypes = Object.entries(issueTypeCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([label, count]) => ({ label, count }));
    return {
      openCount: open.length,
      fixedCount: fixed.length,
      byPriority,
      roomsWithIssuesCount: roomIdsWithOpen.size,
      topIssueTypes,
    };
  }, [maintenanceRequests]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const clean = rooms.filter(r => r.status === 'Clean').length;
    const cleanOccupied = rooms.filter(r => r.status === 'Clean - Occupied').length;
    const dirty = rooms.filter(r => r.status === 'Dirty').length;
    const dirtyStayover = 0; // TODO: Calculate based on actual data
    const dirtyCheckout = 0; // TODO: Calculate based on actual data
    const outOfOrder = rooms.filter(r => r.status === 'Out Of Order').length;
    const totalClean = rooms.filter(r => r.status.includes('Clean')).length;
    const totalDirty = rooms.filter(r => r.status === 'Dirty').length;
    const allRooms = rooms.length;

    return {
      clean,
      cleanOccupied,
      dirty,
      dirtyStayover,
      dirtyCheckout,
      outOfOrder,
      totalClean,
      totalDirty,
      allRooms,
    };
  }, [rooms]);

  // Filter rooms based on selected filter
  const filteredRooms = useMemo(() => {
    if (filterType === 'all') {
      return rooms;
    } else if (filterType === 'dirty') {
      return rooms.filter(room => room.status === 'Dirty');
    } else if (filterType === 'clean') {
      return rooms.filter(room => room.status === 'Clean' || room.status === 'Clean - Occupied');
    } else if (filterType === 'occupied') {
      return rooms.filter(room => room.status === 'Clean - Occupied');
    } else if (filterType === 'outOfOrder') {
      return rooms.filter(room => room.status === 'Out Of Order');
    }
    return rooms;
  }, [rooms, filterType]);

  // Group rooms by floor only
  const groupedRooms = useMemo(() => {
    const grouped = {};
    
    filteredRooms.forEach(room => {
      const floorKey = room.floorName || `Floor ${room.floorNumber || 'Unknown'}`;
      
      if (!grouped[floorKey]) {
        grouped[floorKey] = {
          floorName: floorKey,
          floorNumber: room.floorNumber,
          rooms: [],
        };
      }
      
      grouped[floorKey].rooms.push(room);
    });
    
    // Sort groups by floor number
    return Object.values(grouped).sort((a, b) => {
      return (a.floorNumber || 999) - (b.floorNumber || 999);
    });
  }, [filteredRooms]);

  // Show all groups (grouping is more important than pagination)
  // If needed, we can add pagination later
  const displayGroups = groupedRooms;

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getStatusColor = (status) => {
    if (!status) return '#757575';
    const statusLower = status.toLowerCase();
    if (statusLower.includes('clean') && !statusLower.includes('occupied')) return '#4caf50';
    if (statusLower.includes('clean') && statusLower.includes('occupied')) return '#2196f3';
    if (statusLower.includes('dirty')) return '#f44336';
    if (statusLower.includes('out of order')) return '#ff9800';
    return '#757575';
  };

  const getStatusLabel = (status) => {
    if (!status) return 'Unknown';
    return status;
  };

  const summaryCards = [
    { 
      label: 'TOTAL ROOMS', 
      value: summaryStats.allRooms, 
      color: '#4ce1b6',
      icon: <CleaningServices sx={{ fontSize: 24, color: '#4ce1b6' }} />,
    },
    { 
      label: 'TOTAL CLEAN', 
      value: summaryStats.totalClean, 
      color: '#4caf50',
      icon: <CheckCircleIcon sx={{ fontSize: 24, color: '#4caf50' }} />,
    },
    { 
      label: 'CLEAN', 
      value: summaryStats.clean, 
      color: '#4caf50',
      icon: <CheckCircleIcon sx={{ fontSize: 24, color: '#4caf50' }} />,
    },
    { 
      label: 'CLEAN OCCUPIED', 
      value: summaryStats.cleanOccupied, 
      color: '#2196f3',
      icon: <HotelIcon sx={{ fontSize: 24, color: '#2196f3' }} />,
    },
    { 
      label: 'TOTAL DIRTY', 
      value: summaryStats.totalDirty, 
      color: '#f44336',
      icon: <WarningIcon sx={{ fontSize: 24, color: '#f44336' }} />,
    },
    { 
      label: 'DIRTY', 
      value: summaryStats.dirty, 
      color: '#f44336',
      icon: <WarningIcon sx={{ fontSize: 24, color: '#f44336' }} />,
    },
    { 
      label: 'DIRTY OCCUPIED', 
      value: summaryStats.dirtyStayover, 
      color: '#ff9800',
      icon: <WarningIcon sx={{ fontSize: 24, color: '#ff9800' }} />,
    },
    { 
      label: 'DIRTY CHECKOUT', 
      value: summaryStats.dirtyCheckout, 
      color: '#9c27b0',
      icon: <CancelIcon sx={{ fontSize: 24, color: '#9c27b0' }} />,
    },
  ];

  return (
      <Box sx={getDashboardPageShellSx(currentTheme)}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <DashboardHeader
            title="Housekeeping"
            breadcrumbs={[
              { label: 'Home', path: '/dashboard/' },
              { label: 'Housekeeping', path: HOUSEKEEPING_DEFAULT_PATH },
              { label: HOUSEKEEPING_TABS[activeTab]?.label || 'Room Status' },
            ]}
            navLinks={[
              { url: '/booking/dashboard/', label: 'Booking Dashboard' },
              { url: '/finance/expenses/', label: 'Expenses' },
            ]}
          />
        </Box>

        {/* Tabs */}
        <Paper elevation={0} sx={getHousekeepingTabsPaperSx(isDark)}>
          <NotchTabs
            value={activeTab}
            onChange={handleTabChange}
            tabs={HOUSEKEEPING_TABS}
            accentColor={CUSTOM_BORDER_COLOR}
            sx={{ width: '100%', m: 0 }}
          />
        </Paper>

        {/* Tab Content */}
        {activeTab === 0 && (
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={9}>
            {/* Loading: skeleton for metric cards until we have fetched (avoids 0 → value blink) */}
            {(loading || !hasFetched) && !apiError && (
              <Paper
                elevation={0}
                sx={{
                  ...getHousekeepingPanelPaperSx(isDark, { p: 2, mb: 3 }),
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Skeleton variant="text" width={100} height={20} sx={{ mb: 1 }} />
                    <Skeleton variant="text" width={60} height={48} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Skeleton variant="text" width={80} height={20} sx={{ mb: 1.5 }} />
                    <Grid container spacing={1}>
                      {[1, 2, 3].map((i) => (
                        <Grid item xs={4} key={i}>
                          <Skeleton variant="rounded" height={56} sx={{ borderRadius: 2 }} />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Skeleton variant="text" width={60} height={20} sx={{ mb: 1.5 }} />
                    <Grid container spacing={1}>
                      {[1, 2, 3].map((i) => (
                        <Grid item xs={4} key={i}>
                          <Skeleton variant="rounded" height={56} sx={{ borderRadius: 2 }} />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* Summary Cards and Date/Filter - only after we have data (prevents 0 → value blink) */}
            {!loading && hasFetched && (
              <>
            {/* Summary: creative grouped design */}
            <Paper
              elevation={0}
              sx={{
                ...getHousekeepingPanelPaperSx(isDark, {
                  p: 0,
                  mb: 3,
                  overflow: 'hidden',
                  borderRadius: 3,
                  boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.15)' : '0 4px 24px rgba(0,0,0,0.06)',
                }),
              }}
            >
              <Grid container>
                {/* Hero: Total Rooms */}
                <Grid item xs={12} md={3}>
                  <Box
                    sx={{
                      p: 2.5,
                      height: '100%',
                      minHeight: 140,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      borderRight: { md: `1px solid ${panelBorder}` },
                      borderBottom: { xs: `1px solid ${panelBorder}`, md: 'none' },
                      background: isDark
                        ? `linear-gradient(135deg, rgba(76, 225, 182, 0.08) 0%, transparent 50%)`
                        : `linear-gradient(135deg, rgba(76, 225, 182, 0.06) 0%, transparent 50%)`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <CleaningServices sx={{ fontSize: 28, color: CUSTOM_BORDER_COLOR }} />
                      <Typography variant="overline" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700, letterSpacing: 1.2 }}>
                        Total Rooms
                      </Typography>
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: CUSTOM_BORDER_COLOR, lineHeight: 1.2 }}>
                      {summaryStats.allRooms}
                    </Typography>
                  </Box>
                </Grid>

                {/* Clean section */}
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      p: 2,
                      height: '100%',
                      minHeight: 140,
                      borderRight: { md: `1px solid ${panelBorder}` },
                      borderBottom: { xs: `1px solid ${panelBorder}`, md: 'none' },
                      borderLeft: { md: 'none' },
                      borderLeftWidth: 0,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, pb: 1, borderBottom: `3px solid #4caf50` }}>
                      <CheckCircleIcon sx={{ fontSize: 22, color: '#4caf50' }} />
                      <Typography variant="subtitle2" sx={{ color: isDark ? '#a5d6a7' : '#2e7d32', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                        Clean
                      </Typography>
                    </Box>
                    <Grid container spacing={1.5}>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center', p: 1, borderRadius: 2, backgroundColor: isDark ? 'rgba(76, 175, 80, 0.12)' : 'rgba(76, 175, 80, 0.08)' }}>
                          <Typography variant="caption" sx={{ color: isDark ? '#81c784' : '#388e3c', fontWeight: 600, display: 'block' }}>Total</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#4caf50' }}>{summaryStats.totalClean}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center', p: 1, borderRadius: 2, backgroundColor: isDark ? 'rgba(76, 175, 80, 0.08)' : 'rgba(76, 175, 80, 0.05)' }}>
                          <Typography variant="caption" sx={{ color: isDark ? '#a5d6a7' : '#66bb6a', fontWeight: 600, display: 'block' }}>Clean</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#4caf50' }}>{summaryStats.clean}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center', p: 1, borderRadius: 2, backgroundColor: isDark ? 'rgba(33, 150, 243, 0.12)' : 'rgba(33, 150, 243, 0.08)' }}>
                          <Typography variant="caption" sx={{ color: isDark ? '#64b5f6' : '#1976d2', fontWeight: 600, display: 'block' }}>Occupied</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#2196f3' }}>{summaryStats.cleanOccupied}</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>

                {/* Dirty section */}
                <Grid item xs={12} md={5}>
                  <Box sx={{ p: 2, height: '100%', minHeight: 140 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, pb: 1, borderBottom: `3px solid #f44336` }}>
                      <WarningIcon sx={{ fontSize: 22, color: '#f44336' }} />
                      <Typography variant="subtitle2" sx={{ color: isDark ? '#ef9a9a' : '#c62828', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                        Dirty
                      </Typography>
                    </Box>
                    <Grid container spacing={1.5}>
                      <Grid item xs={4} sm={4}>
                        <Box sx={{ textAlign: 'center', p: 1, borderRadius: 2, backgroundColor: isDark ? 'rgba(244, 67, 54, 0.15)' : 'rgba(244, 67, 54, 0.08)' }}>
                          <Typography variant="caption" sx={{ color: isDark ? '#e57373' : '#d32f2f', fontWeight: 600, display: 'block' }}>Total</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#f44336' }}>{summaryStats.totalDirty}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4} sm={4}>
                        <Box sx={{ textAlign: 'center', p: 1, borderRadius: 2, backgroundColor: isDark ? 'rgba(244, 67, 54, 0.1)' : 'rgba(244, 67, 54, 0.05)' }}>
                          <Typography variant="caption" sx={{ color: isDark ? '#ef9a9a' : '#e57373', fontWeight: 600, display: 'block' }}>Dirty</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#f44336' }}>{summaryStats.dirty}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4} sm={4}>
                        <Box sx={{ textAlign: 'center', p: 1, borderRadius: 2, backgroundColor: isDark ? 'rgba(255, 152, 0, 0.12)' : 'rgba(255, 152, 0, 0.08)' }}>
                          <Typography variant="caption" sx={{ color: isDark ? '#ffb74d' : '#f57c00', fontWeight: 600, display: 'block' }}>Occupied</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#ff9800' }}>{summaryStats.dirtyStayover}</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

        {/* Filter and Total Rooms */}
        <Paper
          elevation={0}
          sx={{
            ...getHousekeepingPanelPaperSx(isDark, {
              p: { xs: 2, sm: 3 },
              mb: 3,
            }),
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: canManageHousekeepingSettings ? 'space-between' : 'flex-end',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            {canManageHousekeepingSettings && (
              <Tooltip
                title={
                  autoCleanOnCheckout
                    ? 'Checkout marks rooms clean and skips housekeeping tasks'
                    : 'Checkout marks rooms dirty and creates housekeeping tasks'
                }
                arrow
                placement="top"
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1.25,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 999,
                    border: `1px solid ${
                      autoCleanOnCheckout
                        ? alpha(CUSTOM_BORDER_COLOR, 0.55)
                        : panelBorder
                    }`,
                    background: autoCleanOnCheckout
                      ? isDark
                        ? `linear-gradient(135deg, ${alpha(CUSTOM_BORDER_COLOR, 0.22)} 0%, ${alpha('#0ea5e9', 0.12)} 100%)`
                        : `linear-gradient(135deg, ${alpha(CUSTOM_BORDER_COLOR, 0.18)} 0%, ${alpha('#0ea5e9', 0.08)} 100%)`
                      : isDark
                        ? alpha('#fff', 0.03)
                        : alpha('#0f172a', 0.03),
                    boxShadow: autoCleanOnCheckout
                      ? `0 0 0 3px ${alpha(CUSTOM_BORDER_COLOR, 0.12)}`
                      : 'none',
                    transition: 'background 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease',
                  }}
                >
                  <AutoAwesomeIcon
                    sx={{
                      fontSize: 18,
                      color: autoCleanOnCheckout ? CUSTOM_BORDER_COLOR : isDark ? '#94a3b8' : '#64748b',
                      transition: 'color 0.25s ease, transform 0.25s ease',
                      transform: autoCleanOnCheckout ? 'rotate(-8deg) scale(1.05)' : 'none',
                    }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        lineHeight: 1.2,
                        color: isDark ? '#e2e8f0' : '#0f172a',
                      }}
                    >
                      Auto clean on checkout
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: isDark ? '#94a3b8' : '#64748b',
                        lineHeight: 1.2,
                        display: 'block',
                      }}
                    >
                      {autoCleanOnCheckout ? 'Housekeeping process off' : 'Housekeeping process on'}
                    </Typography>
                  </Box>
                  <Switch
                    checked={autoCleanOnCheckout}
                    onChange={handleAutoCleanToggle}
                    disabled={autoCleanLoading || autoCleanSaving}
                    size="small"
                    inputProps={{ 'aria-label': 'Auto clean on checkout' }}
                    sx={{
                      ml: 0.5,
                      width: 46,
                      height: 28,
                      p: 0.5,
                      '& .MuiSwitch-switchBase': {
                        p: 0.5,
                        '&.Mui-checked': {
                          color: '#0b1220',
                          transform: 'translateX(18px)',
                          '& + .MuiSwitch-track': {
                            backgroundColor: CUSTOM_BORDER_COLOR,
                            opacity: 1,
                          },
                        },
                      },
                      '& .MuiSwitch-thumb': {
                        width: 18,
                        height: 18,
                        boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                      },
                      '& .MuiSwitch-track': {
                        borderRadius: 999,
                        backgroundColor: isDark ? '#475569' : '#cbd5e1',
                        opacity: 1,
                      },
                    }}
                  />
                </Box>
              </Tooltip>
            )}

            {/* Toggle Buttons and Total Rooms */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              {/* Filter Toggle Buttons */}
              <ToggleButtonGroup
                value={filterType}
                exclusive
                onChange={handleFilterChange}
                aria-label="filter type"
                size="small"
                sx={{
                  height: '36px',
                  '& .MuiToggleButton-root': {
                    borderColor: CUSTOM_BORDER_COLOR,
                    color: isDark ? '#ddd' : '#333',
                    fontSize: '14px',
                    padding: '4px 12px',
                    textTransform: 'none',
                    '&.Mui-selected': {
                      backgroundColor: CUSTOM_BORDER_COLOR,
                      color: '#000',
                      '&:hover': {
                        backgroundColor: CUSTOM_BORDER_COLOR,
                      },
                    },
                  },
                }}
              >
                <ToggleButton value="all" aria-label="all">
                  All
                </ToggleButton>
                <ToggleButton value="dirty" aria-label="dirty">
                  Dirty
                </ToggleButton>
                <ToggleButton value="clean" aria-label="clean">
                  Clean
                </ToggleButton>
                <ToggleButton value="occupied" aria-label="occupied">
                  Occupied
                </ToggleButton>
                <ToggleButton value="outOfOrder" aria-label="outOfOrder">
                  Out Of Order
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        </Paper>
              </>
            )}

        {/* Error State */}
        {!loading && apiError && (
          <ErrorState
            error={apiError}
            statusCode={apiError?.statusCode}
            onRetry={fetchHousekeepingData}
            showHomeButton={true}
          />
        )}
        
        {/* Simple error message for non-critical errors */}
        {error && !apiError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {housekeepingError && !apiError && (
          <Alert severity="error" onClose={() => setHousekeepingError('')} sx={{ mb: 3 }}>
            {housekeepingError}
          </Alert>
        )}

        {loading && !apiError && activeTab !== 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 280, p: 4 }}>
            <CircularProgress sx={{ color: CUSTOM_BORDER_COLOR }} />
          </Box>
        )}

        {/* Rooms Grid - Grouped by Floor and Room Type */}
        {!loading && !apiError && !error && hasFetched && (
          <>
            {filteredRooms.length === 0 ? (
              <EmptyStateCard themeMode={currentTheme}>
                No rooms found
              </EmptyStateCard>
            ) : (
              <>
                {displayGroups.map((group, groupIndex) => (
                  <Accordion
                    key={`${group.floorName}-${groupIndex}`}
                    defaultExpanded={groupIndex === 0}
                    sx={{
                      mb: 2,
                      ...getHousekeepingPanelPaperSx(isDark, { borderRadius: 1, boxShadow: 'none' }),
                      '&:before': {
                        display: 'none',
                      },
                      boxShadow: 'none',
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: isDark ? '#ddd' : '#333' }} />}
                      sx={{
                        borderLeft: `4px solid ${CUSTOM_BORDER_COLOR}`,
                        '& .MuiAccordionSummary-content': {
                          alignItems: 'center',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Typography
                          variant="h6"
                          sx={{
                            color: themeColors.text,
                            fontWeight: 600,
                            fontSize: '1rem',
                          }}
                        >
                          {group.floorName}
                        </Typography>
                        <Chip
                          label={`${group.rooms.length} room${group.rooms.length !== 1 ? 's' : ''}`}
                          size="small"
                          sx={{
                            backgroundColor: themeColors.chipBg,
                            color: themeColors.text,
                            fontSize: '0.7rem',
                            height: 24,
                          }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 1.5, px: { xs: 1, sm: 1.5 }, pb: 1.5 }}>
                      <Grid container spacing={1.5}>
                        {group.rooms.map((room) => {
                          const statusColor = getStatusColor(room.status);
                          const statusLabel = getStatusLabel(room.status);
                          const hasMaintenance = Boolean(room.maintenanceRequests?.length);
                          const isDirtyRoom = room.status === 'Dirty';
                          const issueCount = room.maintenanceRequests?.length || 0;
                          const bookingStatusLabel = room.bookingStatus
                            ? room.bookingStatus.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
                            : '';
                          // Avoid showing booking status when it duplicates clean/occupancy status
                          const showBookingChip = Boolean(
                            bookingStatusLabel
                            && bookingStatusLabel.toLowerCase() !== String(statusLabel).toLowerCase()
                          );
                          return (
                          <Grid item xs={6} sm={4} md={3} lg={2} key={room.id}>
                            <Card
                              elevation={0}
                              onClick={() => handleDirtyRoomClick(room)}
                              sx={{
                                ...getHousekeepingRoomCardSx(isDark, {
                                  hasMaintenance,
                                  accentColor: statusColor,
                                }),
                                cursor: isDirtyRoom ? 'pointer' : 'default',
                                opacity: housekeepingResolving && isDirtyRoom ? 0.72 : 1,
                              }}
                            >
                              <CardContent
                                sx={{
                                  position: 'relative',
                                  flex: 1,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  textAlign: 'center',
                                  px: 1.25,
                                  pt: 1.5,
                                  pb: '12px !important',
                                  gap: 0.65,
                                }}
                              >
                                {hasMaintenance && (
                                  <Chip
                                    icon={<Build sx={{ fontSize: '12px !important' }} />}
                                    label={issueCount}
                                    size="small"
                                    sx={{
                                      position: 'absolute',
                                      top: 8,
                                      right: 8,
                                      height: 20,
                                      minWidth: 28,
                                      fontSize: '0.62rem',
                                      fontWeight: 800,
                                      backgroundColor: alpha('#ff9800', isDark ? 0.25 : 0.16),
                                      color: '#ff9800',
                                      border: `1px solid ${alpha('#ff9800', 0.5)}`,
                                      '& .MuiChip-icon': { color: '#ff9800', ml: 0.5, mr: -0.25 },
                                      '& .MuiChip-label': { px: 0.5 },
                                    }}
                                  />
                                )}

                                <Box
                                  sx={{
                                    position: 'relative',
                                    width: 64,
                                    height: 64,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '50%',
                                    background: `radial-gradient(circle, ${alpha(statusColor, isDark ? 0.22 : 0.16)} 0%, transparent 70%)`,
                                  }}
                                >
                                  <Avatar
                                    src={room.roomTypeImageUrl || undefined}
                                    alt={room.roomTypeName || `Room ${room.roomNumber}`}
                                    imgProps={{ loading: 'lazy', decoding: 'async', style: { objectFit: 'cover' } }}
                                    sx={{
                                      width: 54,
                                      height: 54,
                                      bgcolor: alpha(statusColor, isDark ? 0.14 : 0.1),
                                      color: statusColor,
                                      border: `2.5px solid ${alpha(statusColor, isDark ? 0.75 : 0.55)}`,
                                      boxShadow: isDark
                                        ? `0 6px 14px rgba(0,0,0,0.3)`
                                        : `0 4px 12px ${alpha('#0f172a', 0.1)}`,
                                    }}
                                  >
                                    <Hotel sx={{ fontSize: 24 }} />
                                  </Avatar>
                                </Box>

                                <Typography
                                  sx={{
                                    fontWeight: 800,
                                    fontSize: '1rem',
                                    letterSpacing: 0.3,
                                    color: isDark ? '#f8fafc' : '#0f172a',
                                    lineHeight: 1.15,
                                  }}
                                >
                                  {room.roomNumber}
                                </Typography>

                                <Chip
                                  size="small"
                                  label={statusLabel}
                                  sx={{
                                    height: 22,
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                    backgroundColor: alpha(statusColor, isDark ? 0.2 : 0.12),
                                    color: statusColor,
                                    border: `1px solid ${alpha(statusColor, 0.4)}`,
                                    maxWidth: '100%',
                                    '& .MuiChip-label': {
                                      px: 1,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    },
                                  }}
                                />

                                <Stack
                                  spacing={0.35}
                                  alignItems="center"
                                  sx={{ width: '100%', mt: 0.25, minHeight: 36 }}
                                >
                                  <Typography
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: '0.78rem',
                                      color: room.guestName
                                        ? (isDark ? '#e2e8f0' : '#1e293b')
                                        : (isDark ? '#64748b' : '#94a3b8'),
                                      width: '100%',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      lineHeight: 1.25,
                                    }}
                                  >
                                    {room.guestName || 'Vacant'}
                                  </Typography>

                                  {room.roomTypeName ? (
                                    <Typography
                                      sx={{
                                        fontSize: '0.68rem',
                                        color: isDark ? '#94a3b8' : '#64748b',
                                        width: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        lineHeight: 1.2,
                                      }}
                                    >
                                      {room.roomTypeName}
                                    </Typography>
                                  ) : null}

                                  {room.bookingId ? (
                                    <Typography
                                      sx={{
                                        fontSize: '0.64rem',
                                        fontWeight: 700,
                                        color: isDark ? '#78d9c0' : '#0d9488',
                                        width: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      #{room.bookingId}
                                    </Typography>
                                  ) : null}
                                </Stack>

                                {(room.checkIn || room.checkOut) && (
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    justifyContent="center"
                                    alignItems="center"
                                    sx={{ width: '100%', flexWrap: 'wrap' }}
                                  >
                                    {room.checkIn && (
                                      <Stack direction="row" spacing={0.35} alignItems="center">
                                        <LoginIcon sx={{ fontSize: 12, color: isDark ? '#81c784' : '#388e3c' }} />
                                        <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: isDark ? '#bbb' : '#555' }}>
                                          {room.checkIn}
                                        </Typography>
                                      </Stack>
                                    )}
                                    {room.checkOut && (
                                      <Stack direction="row" spacing={0.35} alignItems="center">
                                        <LogoutIcon sx={{ fontSize: 12, color: isDark ? '#e57373' : '#d32f2f' }} />
                                        <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: isDark ? '#bbb' : '#555' }}>
                                          {room.checkOut}
                                        </Typography>
                                      </Stack>
                                    )}
                                  </Stack>
                                )}

                                {showBookingChip && (
                                  <Chip
                                    size="small"
                                    label={bookingStatusLabel}
                                    sx={{
                                      height: 18,
                                      fontSize: '0.58rem',
                                      fontWeight: 700,
                                      backgroundColor: isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.05),
                                      color: isDark ? '#cbd5e1' : '#475569',
                                      border: `1px solid ${isDark ? alpha('#fff', 0.1) : alpha('#0f172a', 0.08)}`,
                                      '& .MuiChip-label': { px: 0.85 },
                                    }}
                                  />
                                )}

                                {room.staffRemark ? (
                                  <Typography
                                    sx={{
                                      width: '100%',
                                      px: 0.75,
                                      py: 0.4,
                                      borderRadius: '10px',
                                      fontSize: '0.62rem',
                                      fontStyle: 'italic',
                                      color: themeColors.subtleText,
                                      backgroundColor: isDark ? alpha('#fff', 0.05) : alpha('#0f172a', 0.04),
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {room.staffRemark}
                                  </Typography>
                                ) : null}
                              </CardContent>
                            </Card>
                          </Grid>
                          );
                        })}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </>
            )}
          </>
        )}
            </Grid>

            <Grid item xs={12} md={3}>
              <HousekeepingBookingTimeline
                isDark={isDark}
                enabled={activeTab === 0}
                reloadKey={arrivalReloadKey}
                referenceDate={moment().format('YYYY-MM-DD')}
              />
            </Grid>
          </Grid>
        )}

        {activeTab === 1 && (
          <RoomCleaningSection onRefreshRoomStatus={fetchHousekeepingData} />
        )}

        {activeTab === 2 && (
          <HousekeepingStaffSection />
        )}

        {activeTab === 3 && (
          <>
            {/* Maintenance Analytics - theme-matched design */}
            <Paper
              elevation={0}
              sx={{
                ...getHousekeepingPanelPaperSx(isDark, {
                  p: 0,
                  mb: 3,
                  overflow: 'hidden',
                  borderRadius: 3,
                  boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.2)' : '0 4px 24px rgba(0,0,0,0.06)',
                }),
              }}
            >
              <Box
                sx={{
                  px: 2.5,
                  py: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  background: isDark
                    ? 'linear-gradient(135deg, rgba(76, 225, 182, 0.12) 0%, rgba(76, 225, 182, 0.04) 50%, transparent 100%)'
                    : 'linear-gradient(135deg, rgba(76, 225, 182, 0.08) 0%, rgba(76, 225, 182, 0.02) 50%, transparent 100%)',
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)',
                  }}
                >
                  <Build sx={{ fontSize: 26, color: CUSTOM_BORDER_COLOR }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: isDark ? '#e0f7f2' : '#0f172a', letterSpacing: 0.5 }}>
                    Maintenance Analytics
                  </Typography>
                  <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 500 }}>
                    Open issues, fixed count & priority breakdown
                  </Typography>
                </Box>
              </Box>
              <Grid container sx={{ p: 2 }} spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      textAlign: 'center',
                      height: '100%',
                      minHeight: 100,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      backgroundColor: maintenanceAnalytics.openCount > 0
                        ? (isDark ? 'rgba(244,67,54,0.15)' : 'rgba(244,67,54,0.08)')
                        : (isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc'),
                      transition: 'box-shadow 0.2s',
                      '&:hover': { boxShadow: maintenanceAnalytics.openCount > 0 ? '0 4px 12px rgba(244,67,54,0.2)' : 'none' },
                    }}
                  >
                    <Typography variant="overline" sx={{ color: isDark ? '#ef9a9a' : '#c62828', fontWeight: 700, letterSpacing: 1 }}>
                      Open Issues
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: maintenanceAnalytics.openCount > 0 ? '#f44336' : (isDark ? '#666' : '#94a3b8') }}>
                      {maintenanceAnalytics.openCount}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      textAlign: 'center',
                      height: '100%',
                      minHeight: 100,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      backgroundColor: isDark ? 'rgba(76,175,80,0.12)' : 'rgba(76,175,80,0.06)',
                      transition: 'box-shadow 0.2s',
                      '&:hover': { boxShadow: '0 4px 12px rgba(76,175,80,0.2)' },
                    }}
                  >
                    <Typography variant="overline" sx={{ color: isDark ? '#81c784' : '#2e7d32', fontWeight: 700, letterSpacing: 1 }}>
                      Fixed
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#4caf50' }}>
                      {maintenanceAnalytics.fixedCount}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      textAlign: 'center',
                      height: '100%',
                      minHeight: 100,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      backgroundColor: maintenanceAnalytics.roomsWithIssuesCount > 0
                        ? (isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.08)')
                        : (isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc'),
                      transition: 'box-shadow 0.2s',
                      '&:hover': maintenanceAnalytics.roomsWithIssuesCount > 0 ? { boxShadow: `0 4px 12px rgba(76, 225, 182, 0.25)` } : {},
                    }}
                  >
                    <Typography variant="overline" sx={{ color: maintenanceAnalytics.roomsWithIssuesCount > 0 ? (isDark ? '#7dd3c0' : '#0d9488') : (isDark ? '#666' : '#94a3b8'), fontWeight: 700, letterSpacing: 1 }}>
                      Rooms with Issues
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: maintenanceAnalytics.roomsWithIssuesCount > 0 ? CUSTOM_BORDER_COLOR : (isDark ? '#666' : '#94a3b8') }}>
                      {maintenanceAnalytics.roomsWithIssuesCount}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      height: '100%',
                      minHeight: 100,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                    }}
                  >
                    <Typography variant="overline" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700, letterSpacing: 1, display: 'block', mb: 1.5 }}>
                      By Priority (Open)
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                      <Chip label={`High ${maintenanceAnalytics.byPriority.HIGH}`} size="small" sx={{ backgroundColor: '#f44336', color: '#fff', fontWeight: 700 }} />
                      <Chip label={`Med ${maintenanceAnalytics.byPriority.MEDIUM}`} size="small" sx={{ backgroundColor: CUSTOM_BORDER_COLOR, color: '#0f172a', fontWeight: 700 }} />
                      <Chip label={`Low ${maintenanceAnalytics.byPriority.LOW}`} size="small" sx={{ backgroundColor: '#4caf50', color: '#fff', fontWeight: 700 }} />
                    </Box>
                  </Box>
                </Grid>
                {maintenanceAnalytics.topIssueTypes.length > 0 && (
                  <Grid item xs={12}>
                    <Box sx={{ pt: 2 }}>
                      <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1.5 }}>
                        Top issue types (open)
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {maintenanceAnalytics.topIssueTypes.map(({ label, count }) => (
                          <Chip
                            key={label}
                            label={`${label} (${count})`}
                            size="small"
                            sx={{
                              backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                              color: isDark ? CUSTOM_BORDER_COLOR : '#0d9488',
                              fontWeight: 600,
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>

            <MaintenanceRequestSection onRefreshRoomStatus={fetchHousekeepingData} />
          </>
        )}

        {activeTab === 4 && (
          <LostAndFoundSection />
        )}

        {activeTab === 5 && (
          <HousekeepingConfigSection />
        )}

        <DashboardHousekeepingDrawer
          cleaningId={housekeepingCleaningId}
          open={Boolean(housekeepingCleaningId)}
          onClose={handleHousekeepingDrawerClose}
          onSuccess={handleHousekeepingDrawerSuccess}
          onError={handleHousekeepingDrawerError}
          themeMode={currentTheme}
        />
      </Box>
  );
};

export default HousekeepingPage;
